import { useState, useEffect, useCallback } from 'react'
import { DealershipSubscription, SubscriptionInvoice } from '../types'
import { apiService, ApiError } from '../services/api'

interface UseSubscriptionReturn {
  subscription: DealershipSubscription | null
  invoices: SubscriptionInvoice[]
  isLoading: boolean
  error: string | null
  refetch: () => Promise<void>
  refetchInvoices: () => Promise<void>
}

export const useSubscription = (): UseSubscriptionReturn => {
  const [subscription, setSubscription] = useState<DealershipSubscription | null>(null)
  const [invoices, setInvoices] = useState<SubscriptionInvoice[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchSubscription = useCallback(async () => {
    try {
      const data = await apiService.getCurrentSubscription()
      setSubscription(data)
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setSubscription(null)
        return
      }
      const message =
        err instanceof ApiError ? err.message : 'Erro ao carregar assinatura'
      setError(message)
      setSubscription(null)
    }
  }, [])

  const fetchInvoices = useCallback(async () => {
    try {
      const data = await apiService.getSubscriptionInvoices()
      const list = Array.isArray(data) ? data : []
      setInvoices(list)
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setInvoices([])
        return
      }
    }
  }, [])

  const refetch = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    await Promise.all([fetchSubscription(), fetchInvoices()])
    setIsLoading(false)
  }, [fetchSubscription, fetchInvoices])

  const refetchInvoices = useCallback(async () => {
    await fetchInvoices()
  }, [fetchInvoices])

  useEffect(() => {
    void refetch()
  }, [refetch])

  return {
    subscription,
    invoices,
    isLoading,
    error,
    refetch,
    refetchInvoices,
  }
}
